/**
 * The SPA's sign-in flow against the platform auth server (ADR-017): a public client running
 * authorization-code + PKCE, with PAR. Tokens live in memory only (a hard refresh signs in
 * again — silent while the IdP session is alive); the in-flight login (verifier/state/nonce)
 * survives the redirect in sessionStorage and is removed on completion.
 */
import { authConfigFor } from './config';
import { challengeS256, generateVerifier, randomToken } from './pkce';
import { navigate } from './redirect';

export interface Session {
  readonly accessToken: string;
  /** Display-only (decoded, unverified — the API re-validates every call). */
  readonly email: string | null;
}

interface PendingLogin {
  verifier: string;
  state: string;
  nonce: string;
}

const PENDING_KEY = 'testMerchant.pendingLogin';

let current: Session | null = null;

export function getSession(): Session | null {
  return current;
}

export function getAccessToken(): string | null {
  return current?.accessToken ?? null;
}

export function setSession(session: Session | null): void {
  current = session;
}

export function clearSession(): void {
  current = null;
}

/** A sign-in round-trip failed in a way the user can only fix by starting over. */
export class AuthFlowError extends Error {}

const redirectUri = (): string => `${window.location.origin}/auth/callback`;

/**
 * Starts the code flow: pushes the authorization request (PAR) and navigates to the
 * auth server's authorize endpoint, which bounces on to the chosen identity provider
 * (`idp` — one of the ids the auth server's /providers catalog advertises; ADR-018).
 */
export async function beginLogin(idp = 'google'): Promise<void> {
  const { issuer, clientId } = authConfigFor(window.location.hostname);
  const verifier = generateVerifier();
  const state = randomToken();
  const nonce = randomToken();
  const pending: PendingLogin = { verifier, state, nonce };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const form = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    code_challenge: await challengeS256(verifier),
    code_challenge_method: 'S256',
    state,
    nonce,
  });

  const res = await fetch(`${issuer}/par`, { method: 'POST', body: form });
  if (!res.ok) throw new AuthFlowError(`Sign-in could not start (PAR ${res.status}).`);
  const par = (await res.json()) as { request_uri: string };

  navigate(
    `${issuer}/authorize?client_id=${encodeURIComponent(clientId)}` +
      `&request_uri=${encodeURIComponent(par.request_uri)}` +
      `&idp=${encodeURIComponent(idp)}`,
  );
}

/**
 * Completes the flow on /auth/callback: correlates the response with the pending login,
 * exchanges the code (PKCE), and establishes the in-memory session.
 */
export async function completeLogin(query: URLSearchParams): Promise<Session> {
  const raw = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);
  if (raw === null) throw new AuthFlowError('No sign-in is in progress; start again.');
  const pending = JSON.parse(raw) as PendingLogin;

  const code = query.get('code');
  if (code === null || query.get('state') !== pending.state) {
    throw new AuthFlowError('The sign-in response does not match this browser session.');
  }

  const { issuer, clientId } = authConfigFor(window.location.hostname);
  const res = await fetch(`${issuer}/token`, {
    method: 'POST',
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri(),
      client_id: clientId,
      code_verifier: pending.verifier,
    }),
  });
  if (!res.ok) throw new AuthFlowError(`Sign-in could not complete (token ${res.status}).`);
  const tokens = (await res.json()) as { access_token: string; id_token?: string };

  const session: Session = { accessToken: tokens.access_token, email: emailOf(tokens.id_token) };
  setSession(session);
  return session;
}

/** Best-effort email extraction from the ID token, for the header display only. */
export function emailOf(idToken: string | undefined): string | null {
  if (idToken === undefined) return null;
  const parts = idToken.split('.');
  if (parts.length !== 3) return null;
  const segment = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const payload = JSON.parse(
      atob(segment.padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=')),
    ) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : null;
  } catch {
    return null;
  }
}
